import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import PaymentAdminApi from '@/core/services/payment/PaymentAdminApi'
import { handleApiError } from '@/core/helpers/handleApiError'
import { openBlob } from '@/core/helpers/downloadBlob'
import { askConfirm } from '@/core/helpers/askReason'

/**
 * Действия над документами (счёт, акт), общие для всех страниц, где они показываются.
 * onChanged вызывается после успешного изменения: страница перезагружает данные.
 */
export function useDocumentActions(onChanged: () => unknown) {
  const busyId = ref<string | null>(null)

  const run = async (id: string, job: () => Promise<unknown>, success: string) => {
    busyId.value = id
    try {
      await job()
      ElMessage({ type: 'success', message: success })
      await onChanged()
    } catch (error) {
      handleApiError(error)
    } finally {
      busyId.value = null
    }
  }

  /** PDF требует авторизации, поэтому скачиваем как Blob и открываем во вкладке. */
  const openPdf = async (id: string) => {
    busyId.value = id
    try {
      openBlob(await PaymentAdminApi.documents.pdf(id))
    } catch (error) {
      handleApiError(error, 'Не удалось получить PDF')
    } finally {
      busyId.value = null
    }
  }

  const cancel = async (id: string, number: string) => {
    if (await askConfirm('Аннулировать документ', `Документ ${number} будет аннулирован.`, 'Аннулировать')) {
      await run(id, () => PaymentAdminApi.documents.cancel(id), 'Документ аннулирован')
    }
  }

  const issueAct = async (invoiceId: string) => {
    if (await askConfirm('Выпустить акт', 'Акт будет выпущен по оплаченному счёту вручную.', 'Выпустить')) {
      await run(invoiceId, () => PaymentAdminApi.documents.issueAct(invoiceId), 'Акт выпущен')
    }
  }

  const regeneratePdf = (id: string) =>
    run(id, () => PaymentAdminApi.documents.regeneratePdf(id), 'PDF пересобран')

  return { busyId, openPdf, cancel, issueAct, regeneratePdf }
}
